import { useEffect, useId, useMemo, useReducer, useRef, useState } from 'react';
import {
  Box,
  Button,
  Drawer,
  IconButton,
  LinearProgress,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import Close from '@mui/icons-material/Close';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { CommandResultSnackbar, type CommandResult } from '@/components/CommandResultSnackbar';
import { DrawerResizeHandle, useDrawerResize } from '@/components/DrawerResize';
import { DialogValidationErrors } from '@/components/forms/DialogValidationErrors';
import { useHasPermission } from '@/components/PermissionGate';
import { toFormErrors, type ValidationError } from '@/lib/validationErrors';
import {
  onlineImportService,
  type OnlineImportFormat,
  type OnlineImportPreview,
  type OnlineImportResult,
  type OnlineImportTarget,
} from './onlineImportService';
import {
  canImport,
  canPreview,
  initialState,
  mappingsToSend,
  ONLINE_IMPORT_STEPS,
  onlineImportReducer,
  rowsToImport,
  type OnlineImportEvent,
  type OnlineImportPreset,
  type OnlineImportState,
  type OnlineImportStep,
} from './onlineImportState';
import { OnlineImportFileStep } from './OnlineImportFileStep';
import { OnlineImportPreviewStep } from './OnlineImportPreviewStep';
import { OnlineImportResultStep } from './OnlineImportResultStep';

/** Lets the result link to the run it was recorded as. */
const RUN_READ_PERMISSION = 'Integration.IntegrationRun.Read';

/**
 * The panel's width: one for every step, the person's own — dragged at its left edge and
 * remembered in their browser, wherever the panel is opened from.
 */
const WIDTH_STORAGE_KEY = 'nexo.onlineImportDrawer.width';
const DEFAULT_WIDTH = 760;
const MIN_WIDTH = 460;

/** What the panel shows: a step, or the wait for a step's answer. */
type View = OnlineImportStep | 'previewing' | 'importing';

/** The step indicator's position; past the last step every step is done. */
const STEP_INDEX: Record<View, number> = { file: 0, previewing: 1, preview: 1, importing: 2, result: ONLINE_IMPORT_STEPS.length };

interface OnlineImportDrawerProps {
  open: boolean;
  /** The entity imported into, e.g. `Product`. */
  entity: string;
  /** The list's title, e.g. `Products`. */
  title: string;
  /** The list's route; the preview links a row to the record it updates at `${basePath}/${id}`. */
  basePath?: string;
  /** The import profile to start from: its format, and its mapping instead of the suggested one. */
  preset?: OnlineImportPreset;
  onClose: () => void;
  /** Called after an import ran, so the list can refresh. */
  onImported: () => void;
}

interface ReadRequest {
  file: File;
  format: OnlineImportFormat;
  revision: number;
}

/**
 * Import a CSV or XLSX into an entity from its list view or from an import profile
 * (integration_architecture.md, Online import), in a panel from the right that leaves the page —
 * the profile's mapping, the list — in view: choose the file, which is read at once, see its first
 * rows and whether its columns are there, adjust them for this run; preview what each row would do;
 * import, and see what went in. The server records every import as a run; the result links to it.
 * The panel closes any time except while the import runs.
 */
export function OnlineImportDrawer({ open, entity, title, basePath, preset, onClose, onImported }: OnlineImportDrawerProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const titleId = useId();
  const [state, dispatch] = useReducer(onlineImportReducer, preset ?? null, initialState);
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [runningRunId, setRunningRunId] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<CommandResult | null>(null);
  // Without a profile the columns are what there is to check, so they start open.
  const [columnsOpen, setColumnsOpen] = useState(!preset);
  const { width, resizing, startResize } = useDrawerResize({ storageKey: WIDTH_STORAGE_KEY, defaultWidth: DEFAULT_WIDTH, minWidth: MIN_WIDTH });
  const canSeeRuns = useHasPermission(RUN_READ_PERMISSION);
  const revision = useLatest(state.revision);

  const { data: target } = useQuery({
    queryKey: ['onlineImportTarget', entity],
    queryFn: () => onlineImportService.getTarget(entity),
    enabled: open,
  });

  const fail = (error: unknown) => {
    setErrors(toFormErrors(error, t('onlineImport.failed', { defaultValue: 'The file could not be imported.' })));
    setRunningRunId(runningImportOf(error));
  };
  /** The answer to a request the panel has moved on from is dropped, an error as much as a result. */
  const failIfCurrent = (error: unknown, asked: number) => {
    if (asked === revision.current) fail(error);
  };
  const clearIfCurrent = (asked: number) => {
    if (asked === revision.current) setErrors([]);
  };

  const analyze = useMutation({
    mutationFn: (request: ReadRequest) => onlineImportService.analyze(request.file, entity, request.format),
    onSuccess: (analysis, request) => {
      clearIfCurrent(request.revision);
      dispatch({ type: 'analyzed', analysis, revision: request.revision });
    },
    onError: (error, request) => failIfCurrent(error, request.revision),
  });

  // The file is read when it is chosen, and read again when how to read it changes.
  const { mutate: read } = analyze;
  useEffect(() => {
    if (open && state.file && !state.analysis) read({ file: state.file, format: state.format, revision: state.revision });
  }, [open, state.file, state.format, state.analysis, state.revision, read]);

  // The variable is the revision the preview was asked at.
  const preview = useMutation<OnlineImportPreview, unknown, number>({
    mutationFn: () => onlineImportService.preview(
      state.file!, entity, state.format, mappingsToSend(state.mappings), target!.requestTimeoutSeconds),
    onSuccess: (result, asked) => {
      clearIfCurrent(asked);
      dispatch({ type: 'previewed', preview: result, revision: asked });
    },
    onError: failIfCurrent,
  });

  const importFile = useMutation({
    mutationFn: () => onlineImportService.importFile(
      state.file!, entity, state.format, mappingsToSend(state.mappings), target!.requestTimeoutSeconds),
    onSuccess: (result) => {
      setErrors([]);
      dispatch({ type: 'imported', result });
      setOutcome(outcomeOf(result, t));
      onImported();
    },
    onError: fail,
  });

  const importing = importFile.isPending;
  const view = viewOf(state.step, preview.isPending, importing);

  const close = () => {
    if (importing) return;
    dispatch({ type: 'reset' });
    setErrors([]);
    setRunningRunId(null);
    setColumnsOpen(!preset);
    onClose();
  };

  const openRun = (runId: string) => {
    close();
    navigate(`/integration/integrationruns/${runId}`);
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={close}
        slotProps={{
          paper: { role: 'dialog', 'aria-modal': true, 'aria-labelledby': titleId },
          // Light enough that the page behind — the profile's mapping, the list — stays readable.
          backdrop: { sx: { bgcolor: 'rgba(0, 0, 0, 0.28)' } },
        }}
      >
        <DrawerResizeHandle onMouseDown={startResize} resizing={resizing} label={t('toolbar.resizePanel', { defaultValue: 'Resize panel' })} />
        <Box sx={{ width: { xs: '100vw', sm: width }, maxWidth: '100vw', height: '100%', display: 'flex', flexDirection: 'column' }}>
          <PanelHeader titleId={titleId} title={titleOf(state.preset, title, t)} view={view} importing={importing} onClose={close} />
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <DialogValidationErrors errors={errors} onClear={() => setErrors([])} />
            <RunningImportLink runId={runningRunId} canSeeRuns={canSeeRuns} onOpen={openRun} />
            <PanelBody
              view={view}
              state={state}
              target={target}
              dispatch={dispatch}
              basePath={basePath}
              reading={analyze.isPending}
              columnsOpen={columnsOpen}
              onColumnsOpenChange={setColumnsOpen}
            />
          </Box>
          <Box sx={{ px: 2, py: 1.25, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <PanelActions
              view={view}
              state={state}
              target={target}
              busy={analyze.isPending || preview.isPending || importing}
              canSeeRuns={canSeeRuns}
              onClose={close}
              onPreview={() => preview.mutate(state.revision)}
              onImport={() => importFile.mutate()}
              onBack={() => dispatch({ type: 'goTo', step: 'file' })}
              onOpenRun={openRun}
            />
          </Box>
        </Box>
      </Drawer>
      <CommandResultSnackbar
        result={outcome}
        onClose={() => setOutcome(null)}
        action={openRunAction(state.result, canSeeRuns, openRun, t)}
      />
    </>
  );
}

function viewOf(step: OnlineImportStep, previewing: boolean, importing: boolean): View {
  if (importing) return 'importing';
  return previewing ? 'previewing' : step;
}

/** Narrow while a file is chosen, unless its columns are open; wider for the preview, a column per mapped field; wide for the result. */
function titleOf(preset: OnlineImportPreset | null, title: string, t: TFunction): string {
  return preset
    ? t('onlineImport.profileTitle', { defaultValue: 'Import with profile {{name}}', name: preset.name })
    : t('onlineImport.title', { defaultValue: 'Import {{title}} from file', title });
}

/** The snackbar's link to the run the import was recorded as, for someone who may read runs. */
function openRunAction(result: OnlineImportResult | null, canSeeRuns: boolean, onOpen: (runId: string) => void, t: TFunction) {
  return result && canSeeRuns ? (
    <Button color="inherit" size="small" onClick={() => onOpen(result.runId)}>
      {t('onlineImport.openRun', { defaultValue: 'Open run' })}
    </Button>
  ) : undefined;
}

/** After a 409: the same person's import into the same entity that is still running. */
function RunningImportLink({ runId, canSeeRuns, onOpen }: { runId: string | null; canSeeRuns: boolean; onOpen: (runId: string) => void }) {
  const { t } = useTranslation();
  if (!runId || !canSeeRuns) return null;
  return (
    <Button size="small" sx={{ alignSelf: 'flex-start', textTransform: 'none' }} onClick={() => onOpen(runId)}>
      {t('onlineImport.openRunningImport', { defaultValue: 'Open the import that is running' })}
    </Button>
  );
}

/** The latest value, for a callback that runs after the render it was made in. */
function useLatest<T>(value: T) {
  const ref = useRef(value);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref;
}

interface PanelHeaderProps {
  titleId: string;
  title: string;
  view: View;
  importing: boolean;
  onClose: () => void;
}

function PanelHeader({ titleId, title, view, importing, onClose }: PanelHeaderProps) {
  const { t } = useTranslation();
  return (
    <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography id={titleId} component="h2" sx={{ flex: 1, fontSize: '1rem', fontWeight: 500 }}>{title}</Typography>
        <IconButton size="small" aria-label={t('common.close', { defaultValue: 'Close' })} onClick={onClose} disabled={importing}>
          <Close fontSize="small" />
        </IconButton>
      </Box>
      <Stepper activeStep={STEP_INDEX[view]} sx={{ mt: 1.5 }}>
        {ONLINE_IMPORT_STEPS.map((step) => (
          <Step key={step}><StepLabel>{t(`onlineImport.step.${step}`, { defaultValue: step })}</StepLabel></Step>
        ))}
      </Stepper>
    </Box>
  );
}

interface PanelBodyProps {
  view: View;
  state: OnlineImportState;
  target: OnlineImportTarget | undefined;
  dispatch: (event: OnlineImportEvent) => void;
  basePath?: string;
  reading: boolean;
  columnsOpen: boolean;
  onColumnsOpenChange: (open: boolean) => void;
}

function PanelBody({ view, state, target, dispatch, basePath, reading, columnsOpen, onColumnsOpenChange }: PanelBodyProps) {
  const { t } = useTranslation();
  // The mappings the preview ran with: any change to them discards the preview.
  const previewed = useMemo(() => mappingsToSend(state.mappings), [state.mappings]);
  switch (view) {
    case 'file':
      return (
        <OnlineImportFileStep
          state={state}
          target={target}
          dispatch={dispatch}
          reading={reading}
          columnsOpen={columnsOpen}
          onColumnsOpenChange={onColumnsOpenChange}
        />
      );
    case 'previewing':
      return <Waiting text={t('onlineImport.previewing', {
        defaultValue: 'Reading all {{count}} rows and comparing with existing records…', count: state.analysis?.rowCount ?? 0,
      })} />;
    case 'preview':
      return state.preview && target
        ? <OnlineImportPreviewStep preview={state.preview} target={target} mappings={previewed} basePath={basePath} />
        : null;
    case 'importing':
      return <Waiting text={t('onlineImport.importingRows', {
        defaultValue: 'Importing {{count}} rows… Keep this panel open until it is done.', count: rowsToImport(state.preview),
      })} />;
    default:
      return state.result ? <OnlineImportResultStep result={state.result} /> : null;
  }
}

function Waiting({ text }: { text: string }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography variant="body2" color="text.secondary">{text}</Typography>
      <LinearProgress />
    </Box>
  );
}

interface PanelActionsProps {
  view: View;
  state: OnlineImportState;
  target: OnlineImportTarget | undefined;
  busy: boolean;
  canSeeRuns: boolean;
  onClose: () => void;
  onPreview: () => void;
  onImport: () => void;
  onBack: () => void;
  onOpenRun: (runId: string) => void;
}

function PanelActions({ view, state, target, busy, canSeeRuns, onClose, onPreview, onImport, onBack, onOpenRun }: PanelActionsProps) {
  const { t } = useTranslation();
  const cancel = <Button onClick={onClose}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>;
  const importButton = (
    <Button variant="contained" onClick={onImport} disabled={!canImport(state, target) || busy}>
      {t('onlineImport.importRows', { defaultValue: 'Import {{count}} rows', count: rowsToImport(state.preview) })}
    </Button>
  );
  switch (view) {
    case 'file':
      return (<>{cancel}<Button variant="contained" onClick={onPreview} disabled={!canPreview(state, target) || busy}>
        {t('onlineImport.preview.button', { defaultValue: 'Preview' })}
      </Button></>);
    case 'previewing':
      return <>{cancel}{importButton}</>;
    case 'preview':
      return (<><Button onClick={onBack} disabled={busy}>{t('onlineImport.back', { defaultValue: 'Back' })}</Button>{importButton}</>);
    case 'importing':
      return importButton;
    default:
      return <ResultActions result={state.result} canSeeRuns={canSeeRuns} onClose={onClose} onOpenRun={onOpenRun} />;
  }
}

interface ResultActionsProps {
  result: OnlineImportResult | null;
  canSeeRuns: boolean;
  onClose: () => void;
  onOpenRun: (runId: string) => void;
}

function ResultActions({ result, canSeeRuns, onClose, onOpenRun }: ResultActionsProps) {
  const { t } = useTranslation();
  return (<>
    {result && canSeeRuns && <Button onClick={() => onOpenRun(result.runId)}>{t('onlineImport.openRun', { defaultValue: 'Open run' })}</Button>}
    <Button variant="contained" onClick={onClose}>{t('common.close', { defaultValue: 'Close' })}</Button>
  </>);
}

/** The run a 409 names: the same person's import into the same entity that is still running. */
function runningImportOf(error: unknown): string | null {
  const response = (error as { response?: { status?: number; data?: { error?: { data?: Record<string, unknown> } } } })?.response;
  const runId = response?.status === 409 ? response.data?.error?.data?.runId : undefined;
  return typeof runId === 'string' ? runId : null;
}

function outcomeOf(result: OnlineImportResult, t: TFunction): CommandResult {
  return {
    severity: result.failed === 0 ? 'success' : 'warning',
    message: t('onlineImport.resultSummary', {
      defaultValue: '{{created}} created, {{updated}} updated, {{failed}} failed.',
      created: result.created, updated: result.updated, failed: result.failed,
    }),
  };
}
